// Simplified income tab for meetups: multiple Standard-style ticket tiers
// (single price each — no min/max) and a flat list of sponsors (name +
// amount, one per row). No add-ons, no scenarios.

import { formatMoney } from '../lib/currency';
import { newId } from '../lib/defaults';
import type {
  EventData,
  SponsorTier,
  TicketTier,
  VatMode,
} from '../lib/types';
import NumberField from './NumberField';

type Props = {
  data: EventData;
  currency: string;
  vatMode: VatMode;
  vatRate: number;
  onChange: (patch: (d: EventData) => EventData) => void;
};

export default function MeetupIncomesTab({
  data,
  currency,
  vatMode,
  vatRate,
  onChange,
}: Props) {
  // ---- ticket tier helpers ---------------------------------------------
  function patchTicket(idx: number, patch: Partial<TicketTier>) {
    onChange((d) => ({
      ...d,
      tickets: d.tickets.map((t, i) => {
        if (i !== idx) return t;
        // Single-price model: mirror priceMin and priceMax so downstream
        // calc functions (which use a min/max range) just see a degenerate
        // range collapsed to a single value.
        const next = { ...t, ...patch };
        if (typeof patch.priceMin === 'number') next.priceMax = patch.priceMin;
        if (typeof patch.priceMax === 'number') next.priceMin = patch.priceMax;
        return next;
      }),
    }));
  }
  function addTicket() {
    onChange((d) => {
      const n = d.tickets.length + 1;
      return {
        ...d,
        tickets: [
          ...d.tickets,
          {
            id: newId('t'),
            name: n === 1 ? 'Standard' : `Tier ${n}`,
            qty: 0,
            priceMin: 0,
            priceMax: 0,
          },
        ],
      };
    });
  }
  function removeTicket(idx: number) {
    onChange((d) => ({
      ...d,
      tickets: d.tickets.filter((_, i) => i !== idx),
    }));
  }

  // ---- sponsor helpers --------------------------------------------------
  function patchSponsor(idx: number, patch: Partial<SponsorTier>) {
    onChange((d) => ({
      ...d,
      sponsors: d.sponsors.map((s, i) => {
        if (i !== idx) return s;
        const next = { ...s, ...patch, qty: 1 };
        if (typeof patch.priceMin === 'number') next.priceMax = patch.priceMin;
        return next;
      }),
    }));
  }
  function addSponsor() {
    onChange((d) => {
      const n = d.sponsors.length + 1;
      return {
        ...d,
        sponsors: [
          ...d.sponsors,
          {
            id: newId('s'),
            name: `Sponsor ${n}`,
            qty: 1,
            priceMin: 0,
            priceMax: 0,
          },
        ],
      };
    });
  }
  function removeSponsor(idx: number) {
    onChange((d) => ({
      ...d,
      sponsors: d.sponsors.filter((_, i) => i !== idx),
    }));
  }

  // ---- derived totals ---------------------------------------------------
  const ticketRevenue = data.tickets.reduce((a, t) => a + t.qty * t.priceMin, 0);
  const ticketCount = data.tickets.reduce((a, t) => a + t.qty, 0);
  const sponsorRevenue = data.sponsors.reduce((a, s) => a + s.qty * s.priceMin, 0);
  const totalNet = ticketRevenue + sponsorRevenue;
  const showVat = vatMode === 'gross';
  const vatTotal = showVat ? totalNet * vatRate : 0;
  const totalGross = totalNet + vatTotal;
  const denom = showVat ? totalGross : totalNet;

  return (
    <div className="row-stack">
      <div className="card">
        <h2>
          Revenue breakdown
          <span className="muted section-meta-tag">
            · {showVat ? `net + ${(vatRate * 100).toFixed(0)}% VAT` : 'net of VAT'}
          </span>
        </h2>
        <div className="breakdown">
          <BarRow
            cls="tickets"
            label="Tickets"
            netAmount={ticketRevenue}
            denom={denom}
            currency={currency}
            showVat={showVat}
            vatRate={vatRate}
          />
          <BarRow
            cls="sponsors"
            label="Sponsors"
            netAmount={sponsorRevenue}
            denom={denom}
            currency={currency}
            showVat={showVat}
            vatRate={vatRate}
          />
        </div>

        {showVat ? (
          <div className="sum-bar sum-bar-stacked">
            <span>Total revenue</span>
            <div className="sum-vat-breakdown">
              <span className="sum-net">Net {formatMoney(totalNet, currency)}</span>
              <span className="sum-sep">·</span>
              <span className="sum-vat">VAT {formatMoney(vatTotal, currency)}</span>
              <span className="sum-sep">·</span>
              <span className="total">Gross {formatMoney(totalGross, currency)}</span>
            </div>
          </div>
        ) : (
          <div className="sum-bar">
            <span>Total revenue</span>
            <span className="total">{formatMoney(totalNet, currency)}</span>
          </div>
        )}
      </div>

      <div className="card-grid">
        {/* ---- Tickets ---- */}
        <div className="card">
          <div className="section-head">
            <h2>Tickets</h2>
            <button className="btn ghost sm" onClick={addTicket}>
              + Add tier
            </button>
          </div>

          <div className="meetup-tier-row-header">
            <span>Tier name</span>
            <span>Qty</span>
            <span>Price</span>
            <span></span>
          </div>
          <div className="meetup-tier-rows">
            {data.tickets.map((t, idx) => (
              <div key={t.id} className="meetup-tier-row">
                <input
                  className="input"
                  placeholder="Tier name"
                  value={t.name}
                  onChange={(e) => patchTicket(idx, { name: e.target.value })}
                />
                <NumberField
                  value={t.qty}
                  onChange={(n) => patchTicket(idx, { qty: n })}
                />
                <NumberField
                  value={t.priceMin}
                  step={1}
                  onChange={(n) => patchTicket(idx, { priceMin: n })}
                />
                <button
                  className="btn sm danger"
                  onClick={() => removeTicket(idx)}
                  title="Remove tier"
                  type="button"
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          <div className="sum-bar">
            <span>Σ Tickets ({ticketCount} {ticketCount === 1 ? 'ticket' : 'tickets'})</span>
            <span className="total">{formatMoney(ticketRevenue, currency)}</span>
          </div>
        </div>

        {/* ---- Sponsors ---- */}
        <div className="card">
          <div className="section-head">
            <h2>Sponsors</h2>
            <button className="btn ghost sm" onClick={addSponsor}>
              + Add sponsor
            </button>
          </div>

          <div className="meetup-sponsor-rows">
            {data.sponsors.map((s, idx) => (
              <div key={s.id} className="meetup-sponsor-row">
                <input
                  className="input"
                  placeholder={`Sponsor ${idx + 1}`}
                  value={s.name}
                  onChange={(e) => patchSponsor(idx, { name: e.target.value })}
                />
                <NumberField
                  value={s.priceMin}
                  onChange={(n) => patchSponsor(idx, { priceMin: n })}
                />
                <button
                  className="btn sm danger"
                  onClick={() => removeSponsor(idx)}
                  title="Remove sponsor"
                  type="button"
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          <div className="sum-bar">
            <span>
              Σ Sponsors ({data.sponsors.length}{' '}
              {data.sponsors.length === 1 ? 'sponsor' : 'sponsors'})
            </span>
            <span className="total">{formatMoney(sponsorRevenue, currency)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function BarRow({
  cls,
  label,
  netAmount,
  denom,
  currency,
  showVat,
  vatRate,
}: {
  cls: string;
  label: string;
  netAmount: number;
  denom: number;
  currency: string;
  showVat: boolean;
  vatRate: number;
}) {
  const vat = showVat ? netAmount * vatRate : 0;
  const gross = netAmount + vat;
  const netPct = denom > 0 ? (netAmount / denom) * 100 : 0;
  const vatPct = denom > 0 ? (vat / denom) * 100 : 0;
  const n = Math.max(0, Math.min(100, Math.round(netPct)));
  const v = Math.max(0, Math.min(100, Math.round(vatPct)));
  return (
    <div className="breakdown-row">
      <div className="top">
        <span>{label}</span>
        <span className="amt">
          {showVat ? (
            <>
              <span className="muted">net</span> {formatMoney(netAmount, currency)}
              <span className="muted"> · VAT</span> {formatMoney(vat, currency)}
              <span className="muted"> · gross</span> {formatMoney(gross, currency)}
            </>
          ) : (
            formatMoney(netAmount, currency)
          )}
        </span>
      </div>
      <div className={`bar ${cls} ${showVat ? 'has-vat' : ''}`}>
        <div className={`bar-net w-pct-${n}`} />
        {showVat && <div className={`bar-vat w-pct-${v}`} />}
      </div>
    </div>
  );
}
